import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as Bahraini Dinar (BHD) with 3 decimal places
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., "BHD 9.400")
 */
export function formatBHD(amount: number): string {
  return `BHD ${amount.toFixed(3)}`;
}

/**
 * Type validation and UUID utilities
 */

/**
 * UUID v4 regex pattern
 * Matches format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
const UUID_V4_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID v4
 * @param value - String to validate
 * @returns true if valid UUID v4, false otherwise
 */
export function isValidUUID(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return UUID_V4_REGEX.test(value);
}

/**
 * Generate a cryptographically secure UUID v4
 * Uses browser's crypto API for secure random generation
 * @returns A valid UUID v4 string
 */
export function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

/**
 * Ensure a value is a valid UUID string
 * Converts numbers to strings and validates UUIDs
 * @param value - Value to convert/validate
 * @param fieldName - Field name for error messages
 * @returns Valid UUID string
 * @throws Error if value cannot be converted to valid UUID
 */
export function ensureUUID(value: unknown, fieldName: string = 'ID'): string {
  // Convert number to string
  if (typeof value === 'number') {
    throw new Error(`${fieldName} must be a UUID string, got number: ${value}`);
  }

  // Ensure it's a string
  if (typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string, got: ${typeof value}`);
  }

  // Validate UUID format
  if (!isValidUUID(value)) {
    throw new Error(`${fieldName} is not a valid UUID: ${value}`);
  }

  return value;
}

/**
 * Safely convert product ID to string (handles legacy integer IDs)
 * @param productId - Product ID (string or number)
 * @returns Product ID as string
 */
export function normalizeProductId(productId: string | number): string {
  if (typeof productId === 'number') {
    console.warn('Product ID should be a UUID string, got number:', productId);
    return String(productId);
  }
  return productId;
}

/**
 * Type guard to check if a value is a string
 * @param value - Value to check
 * @returns true if value is string
 */
export function isString(value: unknown): value is string {
  return typeof value === 'string';
}
